/**
 * Which Auto-Renew plan card is the business's current plan.
 *
 * Plans are listed from Plan Management. A card's Razorpay plan ID changes
 * after the plan's first subscription (it was null before) and after every
 * price change, so the current plan is matched on the Plan Management plan ID
 * that /subscription/info returns as `plan_ref`, or on the exact Razorpay plan
 * ID — never on a substring.
 */

/**
 * @param {object} plan a card from the Auto-Renew plan list (plan_id, razorpay_plan_id)
 * @param {object|null} subscriptionInfo /subscription/info result (plan_ref, plan_id)
 * @param {string|null} storedPlanId the plan ID saved when the seller subscribed,
 *   used only until /subscription/info knows the subscription
 * @returns {boolean}
 */
export const isCurrentPlan = (plan, subscriptionInfo, storedPlanId = null) => {
    if (subscriptionInfo && subscriptionInfo.plan_id) {
        if (subscriptionInfo.plan_ref) return plan.plan_id === subscriptionInfo.plan_ref;
        return !!plan.razorpay_plan_id && plan.razorpay_plan_id === subscriptionInfo.plan_id;
    }
    if (!storedPlanId) return false;
    return storedPlanId === plan.plan_id || (!!plan.razorpay_plan_id && storedPlanId === plan.razorpay_plan_id);
};

/**
 * The plan cards to show: the offered yearly plans, plus the business's own
 * plan when it is no longer offered (e.g. a legacy monthly subscription), so a
 * paying subscriber always sees their plan.
 * @param {Array<object>} plans offered plans
 * @param {object|null} subscriptionInfo /subscription/info result
 * @returns {Array<object>}
 */
export const plansWithCurrent = (plans, subscriptionInfo) => {
    // Only a paid subscription (same rule as the page's "PAID" badge); one still
    // being set up (created/authenticated) isn't shown as a plan of its own
    const subscribed = subscriptionInfo && subscriptionInfo.plan_id && !subscriptionInfo.isCancelled
        && subscriptionInfo.status !== 'cancelled'
        && (subscriptionInfo.paid_count > 0 || subscriptionInfo.first_payment_discount_amount > 0);
    if (!subscribed || plans.some(plan => isCurrentPlan(plan, subscriptionInfo))) return plans;
    return [{
        plan_id: subscriptionInfo.plan_ref || null,
        razorpay_plan_id: subscriptionInfo.plan_id,
        plan_name: subscriptionInfo.plan_name,
        price: subscriptionInfo.price,
        billing_period: subscriptionInfo.billing_period || 'monthly',
        limits: subscriptionInfo.limits || [],
        features: [],
        is_current_only: true,
    }, ...plans];
};

/**
 * Text for the Razorpay checkout and messages: "Yearly", "Monthly", ...
 * @param {string} billingPeriod
 */
export const periodLabel = (billingPeriod) => ({
    yearly: 'Yearly',
    quarterly: 'Quarterly',
    monthly: 'Monthly',
}[billingPeriod] || 'Yearly');

/** "year", "month", ... for "next year" style messages */
export const periodNoun = (billingPeriod) => ({
    yearly: 'year',
    quarterly: 'quarter',
    monthly: 'month',
}[billingPeriod] || 'year');
